import { FormStatus } from '@dnb/eufemia';
import { PUBLISHED_STATUS } from '@portals/shared/admin/ContentDto';
import { useEufemiaForm } from '@portals/shared-frontend/hooks';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { createOrUpdatePage } from '@/api/content';
import LoadingModal from '@/components/LoadingModal';
import ModalPage from '@/components/ModalPage';
import { useUser } from '@/hooks/useUser';
import { ApiError } from '@/request';

import AddPageForm from '../AddPageForm';
import { mapFromVisibility, schema, VISIBLE_TO } from '../constants';

export default function AddNewPage() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { teamId } = useParams();
  const { user, isLoading: loadingUser } = useUser();
  const navigate = useNavigate();
  const { controller, handleSubmit, submitting } = useEufemiaForm(schema, {
    content: '',
    publishedStatus: PUBLISHED_STATUS.DRAFT,
    visibleTo: VISIBLE_TO.DNB,
  });

  const onSubmit = handleSubmit(async ({ title, content, publishedStatus, visibleTo }) => {
    if (teamId) {
      const visibility = mapFromVisibility(visibleTo, user?.organizationId, teamId);

      const data = {
        title: title,
        isPublic: visibility.isPublic ?? false,
        blob: { content: content ?? '' },
        publishedStatus: publishedStatus,
        organizations: visibility.organizationId ? [visibility.organizationId] : [],
        sortIndex: 100,
        // No path or parent: the server derives them from the team's content prefix, which
        // depends on whether the team has claimed a slug.
        parentId: null,
      };
      try {
        const result = await createOrUpdatePage(teamId, data);
        navigate(`/team/${teamId}/pages/${result.id}`, { replace: true });
      } catch (error) {
        ApiError.isApiError(error)
          ? setErrorMessage(error.message)
          : setErrorMessage('Something went wrong, please try again later');
      }
    }
  });

  return (
    <ModalPage actualModal onClose={() => navigate(`..`)} skeleton={loadingUser} title="Add new page">
      {submitting && <LoadingModal />}
      <form onSubmit={onSubmit}>
        <AddPageForm controller={controller} />
        {errorMessage && <FormStatus text={errorMessage} top="medium" />}
      </form>
    </ModalPage>
  );
}
